import { LiveFrame } from '../components/junctions/LiveFrame';
import { DarshanJourneySummary } from './DarshanJourneySummary';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck,
  Video,
  Search,
  CheckCircle2,
  ArrowLeft,
  X,
  CameraOff,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  MonitorPlay,
  Clock
} from 'lucide-react';
import baseapi, { API_BASE, useApiOutage } from '../baseapi';

/* ---------- /api/sectors types (backend/app/sectors.py) ---------- */
type Level = 'normal' | 'busy' | 'alert';
type SectorState = 'live' | 'on_demand' | 'offline' | 'no_camera';

interface SectorCamera {
  id: string;
  name: string;
  ip: string;
  checkpoint: string;
  state: string;
  people_in_view: number | null;
  density: number | null;
  today: { toward: number; away: number };
}

interface Sector {
  id: string;
  zone: string;
  name: string;
  exits: number | null;
  features: string[];
  state: SectorState;
  source: 'camera' | 'none';
  cameras: SectorCamera[];
  cameras_total: number;
  cameras_online: number;
  flow: { toward_per_min: number; away_per_min: number; toward_15m: number; away_15m: number } | null;
  today: { toward: number; away: number } | null;
  in_view_now: number | null;
  /** entering / leaving / inside (backend app/sectors.py sector_counts) */
  counts?: {
    inside: number | null; inside_basis: string | null;
    entering_per_min: number | null; leaving_per_min: number | null; net_per_min: number | null;
    entered_today: number | null; left_today: number | null; entered_15m: number | null; left_15m: number | null;
    cameras_down: string[];
  } | null;
  density: number | null;
  status: Level | null;
}

interface Zone {
  z: string;
  name: string;
  name_te: string;
  sectors: number;
  sectors_covered: number;
  cameras: number;
  cameras_online: number;
  exits: number;
}

interface SectorsResponse {
  server_time: number;
  summary: {
    sectors: number;
    sectors_covered: number;
    cameras: number;
    cameras_online: number;
    exits: number;
    status: Record<Level, number>;
    no_camera: number;
    density_configured: boolean;
  };
  density_thresholds: { busy: number; alert: number };
  zones: Zone[];
  sectors: Sector[];
}

type Filter = 'all' | 'live' | 'no_camera' | Level;

const REFRESH_MS = 2000;            // 16 Oct 2026: sector counts as live as the backend gives them (Overview pace)
const fmt = (n: number | null | undefined) => (n == null ? '—' : Math.round(n).toLocaleString('en-IN'));

/** Card look for a sector: measured status first, else live / offline / no camera. */
function look(s: Sector) {
  if (s.status === 'alert') return { badge: 'CRITICAL', tone: 'rose' as const };
  if (s.status === 'busy') return { badge: 'BUSY', tone: 'amber' as const };
  if (s.status === 'normal') return { badge: 'OPTIMAL', tone: 'emerald' as const };
  if (s.state === 'live') return { badge: 'LIVE', tone: 'blue' as const };
  if (s.state === 'on_demand') return { badge: 'ON DEMAND', tone: 'blue' as const };   // view-only cameras, reachable
  if (s.state === 'offline') return { badge: 'OFFLINE', tone: 'rose' as const };
  return { badge: 'NO CAMERA', tone: 'neutral' as const };
}

const TONE = {
  rose: { card: 'bg-rose-50/30 dark:bg-rose-950/15 border-rose-500/40', badge: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30', dot: 'bg-rose-500', bar: 'bg-rose-500' },
  amber: { card: 'bg-amber-50/30 dark:bg-amber-950/15 border-amber-500/40', badge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30', dot: 'bg-amber-500', bar: 'bg-amber-500' },
  emerald: { card: 'bg-white dark:bg-[#181b22] border-neutral-200/80 dark:border-[#262b37]', badge: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30', dot: 'bg-emerald-500', bar: 'bg-emerald-500' },
  blue: { card: 'bg-white dark:bg-[#181b22] border-blue-500/30', badge: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30', dot: 'bg-blue-500', bar: 'bg-blue-500' },
  neutral: { card: 'bg-neutral-50/60 dark:bg-[#15171d] border-dashed border-neutral-200/80 dark:border-[#262b37]', badge: 'bg-neutral-500/10 text-neutral-500 dark:text-neutral-400 border-neutral-500/20', dot: 'bg-neutral-400', bar: 'bg-neutral-400' },
};

/* ---------- live camera viewer for one sector ---------- */
const SectorCameraModal: React.FC<{ sector: Sector; onClose: () => void }> = ({ sector, onClose }) => {
  const [camId, setCamId] = useState(sector.cameras[0]?.id);
  const sectorImgRef = React.useRef<HTMLImageElement | null>(null);
  const cam = sector.cameras.find(c => c.id === camId);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-xs z-50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="w-[860px] max-w-full bg-white dark:bg-[#181b22] border border-neutral-200 dark:border-[#262b37] rounded-xl shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-3.5 py-2.5 bg-neutral-50 dark:bg-[#14171f] border-b border-neutral-200/80 dark:border-[#262b37]">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="px-2.5 py-0.5 rounded bg-blue-500 text-white font-mono text-xs sm:text-sm font-bold">{sector.id}</span>
            <div className="min-w-0">
              <h3 className="text-xs sm:text-sm font-bold text-neutral-900 dark:text-neutral-100 truncate">{sector.name}</h3>
              <span className="text-xs text-neutral-400 font-mono">
                Zone {sector.zone} · {sector.cameras_online}/{sector.cameras_total} cameras online
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-200/50 dark:hover:bg-[#202532] cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-neutral-200/80 dark:border-[#262b37]">
          {sector.cameras.map(c => (
            <button
              key={c.id}
              onClick={() => setCamId(c.id)}
              title={c.name}
              className={`px-2.5 py-1 rounded text-xs font-mono font-semibold border cursor-pointer flex items-center gap-1.5 ${
                c.id === camId
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-neutral-50 dark:bg-[#14171f] text-neutral-600 dark:text-neutral-300 border-neutral-200/60 dark:border-[#262b37] hover:border-blue-400'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${c.state === 'online' ? 'bg-emerald-400' : c.state === 'standby' ? 'bg-teal-400' : 'bg-rose-400'}`} />
              {c.id}
            </button>
          ))}
        </div>

        <div className="relative aspect-video bg-neutral-950">
          {cam && (
            <LiveFrame
              key={cam.id}
              ref={sectorImgRef}
              src={`${API_BASE}cameras/${encodeURIComponent(cam.id)}/snapshot.jpg`}
              alt={cam.name}
              className="absolute inset-0 w-full h-full object-contain"
            />
          )}
        </div>

        {cam && (
          <div className="p-2.5 px-3.5 bg-neutral-50 dark:bg-[#14171f] border-t border-neutral-200/80 dark:border-[#262b37] flex flex-wrap items-center justify-between gap-2 text-xs sm:text-[12.5px] font-mono">
            <span className="text-neutral-600 dark:text-neutral-300 truncate">{cam.name} · {cam.ip}</span>
            <span className="flex items-center gap-3 text-neutral-500">
              <span>In view: <b className="text-neutral-800 dark:text-neutral-100">{fmt(cam.people_in_view)}</b></span>
              <span>Today: <b className="text-emerald-500">{fmt(cam.today.toward)}</b> / <b className="text-sky-500">{fmt(cam.today.away)}</b></span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/** Sector video wall (16 Oct 2026, operators): every sector's cameras one after another, sector by sector (S-1 start
 *  camera, S-1 end camera, S-2 ...), each for `every` seconds, with the sector's live figures over the picture. One
 *  camera at a time, so it costs the cameras and the server no more than one open camera view. Cameras that are
 *  down are skipped. Keys: space = pause / play, left / right = previous / next, Esc = close. */
const SectorVideoWall: React.FC<{ sectors: Sector[]; onClose: () => void }> = ({ sectors, onClose }) => {
  const items = useMemo(() => sectors.flatMap(s => s.cameras
    .filter(c => c.state !== 'unavailable' && c.state !== 'no_camera')
    .map((c, i, all) => ({ sectorId: s.id, camId: c.id, pos: i + 1, of: all.length }))), [sectors]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [every, setEvery] = useState(10);
  const [left, setLeft] = useState(10);
  const boxRef = React.useRef<HTMLDivElement | null>(null);
  const n = items.length;
  const cur = n ? items[idx % n] : null;
  const sector = cur ? sectors.find(s => s.id === cur.sectorId) : undefined;   // live figures (parent refreshes)
  const cam = sector?.cameras.find(c => c.id === cur?.camId);
  const go = useCallback((d: number) => { if (n) { setIdx(i => (i + d + n) % n); setLeft(every); } }, [n, every]);

  useEffect(() => { setLeft(every); }, [every]);
  useEffect(() => {
    if (!playing || !n) return;
    const t = setInterval(() => setLeft(l => {
      if (l <= 1) { setIdx(i => (i + 1) % n); return every; }
      return l - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [playing, n, every]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === ' ') { e.preventDefault(); setPlaying(p => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, go]);

  const c = sector?.counts;
  const chip = (label: string, value: React.ReactNode, tone: string) => (
    <div className="px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur-sm border border-white/10">
      <div className="text-[10.5px] uppercase tracking-wider text-white/60 font-semibold">{label}</div>
      <div className={`font-black font-mono text-xl sm:text-2xl leading-tight ${tone}`}>{value}</div>
    </div>
  );
  return (
    <div ref={boxRef} className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-neutral-950 border-b border-white/10 text-white">
        <div className="flex items-center gap-2.5 min-w-0">
          <MonitorPlay className="w-5 h-5 text-blue-400 shrink-0" />
          {sector && <span className="px-2.5 py-0.5 rounded bg-blue-500 font-mono text-sm font-bold shrink-0">{sector.id}</span>}
          <div className="min-w-0">
            <div className="font-bold text-sm sm:text-base truncate">{sector?.name ?? 'No camera to show'}</div>
            {cur && cam && <div className="text-xs text-white/60 font-mono truncate">Camera {cur.pos} of {cur.of} · {cam.name} · picture {idx % n + 1} / {n}</div>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => go(-1)} title="Previous (left arrow)" className="p-1.5 rounded-md hover:bg-white/10 cursor-pointer"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => setPlaying(p => !p)} title="Pause / play (space)" className="p-1.5 rounded-md hover:bg-white/10 cursor-pointer">
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button onClick={() => go(1)} title="Next (right arrow)" className="p-1.5 rounded-md hover:bg-white/10 cursor-pointer"><ChevronRight className="w-5 h-5" /></button>
          <select value={every} onChange={e => setEvery(Number(e.target.value))} title="Seconds per camera"
            className="ml-1 bg-neutral-900 border border-white/15 rounded-md text-xs font-mono px-1.5 py-1 cursor-pointer">
            {[5, 10, 15, 20, 30, 60].map(v => <option key={v} value={v}>{v} s</option>)}
          </select>
          <button onClick={() => { const el = boxRef.current; if (!document.fullscreenElement) el?.requestFullscreen?.(); else document.exitFullscreen?.(); }}
            title="Full screen" className="p-1.5 rounded-md hover:bg-white/10 cursor-pointer"><Maximize2 className="w-5 h-5" /></button>
          <button onClick={onClose} title="Close (Esc)" className="p-1.5 rounded-md hover:bg-white/10 cursor-pointer"><X className="w-5 h-5" /></button>
        </div>
      </div>
      {/* the camera list on top, under the title bar (operators, 16 Oct 2026) */}
      <div className="flex gap-1 px-2 py-1.5 bg-neutral-950 border-b border-white/10 overflow-x-auto">
        {items.map((it, i) => (
          <button key={`${it.sectorId}:${it.camId}`} onClick={() => { setIdx(i); setLeft(every); }}
            className={`shrink-0 px-2 py-0.5 rounded text-[11px] font-mono cursor-pointer border ${i === idx % n
              ? 'bg-blue-500 border-blue-500 text-white' : 'bg-neutral-900 border-white/10 text-white/60 hover:text-white'}`}>
            {it.sectorId} · {it.camId}
          </button>
        ))}
      </div>
      <div className="relative flex-1 min-h-0 bg-black">
        {cam ? (
          <LiveFrame key={`${cur!.sectorId}:${cam.id}`} src={`${API_BASE}cameras/${encodeURIComponent(cam.id)}/snapshot.jpg`}
            alt={cam.name} className="absolute inset-0 w-full h-full object-contain" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">No sector camera is online</div>
        )}
        {sector && (
          <div className="absolute left-3 bottom-3 flex flex-wrap gap-2">
            {chip('People now', fmt(c?.inside), 'text-white')}
            {chip('Entering /min', fmt(c?.entering_per_min), 'text-emerald-400')}
            {chip('Leaving /min', fmt(c?.leaving_per_min), 'text-sky-400')}
            {cam && chip('In view on this camera', fmt(cam.people_in_view), 'text-amber-300')}
          </div>
        )}
        {playing && n > 1 && (
          <div className="absolute left-0 right-0 bottom-0 h-1 bg-white/10">
            <div className="h-full bg-blue-500 transition-all duration-1000 ease-linear" style={{ width: `${100 * (every - left + 1) / every}%` }} />
          </div>
        )}
      </div>
    </div>
  );
};

export const SectorReadinessView: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [data, setData] = useState<SectorsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const outage = useApiOutage();     // true only after 2 minutes without an answer: a restart shows no message
  const [, setUpdatedAt] = useState<string>('');
  const [selectedZone, setSelectedZone] = useState<string>('all');
  const [filter, setFilter] = useState<Filter>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [openSector, setOpenSector] = useState<Sector | null>(null);
  const [wallOpen, setWallOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await baseapi.get('/sectors');
      if (!res.data || !Array.isArray(res.data.sectors)) throw new Error('bad response');
      setData(res.data);
      setError(null);
      setUpdatedAt(new Date().toLocaleTimeString('en-GB'));
    } catch {
      setError('Sector API not reachable — the backend may need a restart to enable /api/sectors.');
    }
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  const sectors = data?.sectors ?? [];
  const sum = data?.summary;
  const zones = data?.zones ?? [];

  const counts = useMemo(() => ({
    all: sectors.length,
    live: sectors.filter(s => s.source === 'camera').length,
    no_camera: sectors.filter(s => s.state === 'no_camera').length,
    normal: sum?.status.normal ?? 0,
    busy: sum?.status.busy ?? 0,
    alert: sum?.status.alert ?? 0,
  }), [sectors, sum]);

  const filtered = sectors.filter(s => {
    if (selectedZone !== 'all' && s.zone !== selectedZone) return false;
    if (filter === 'live' && s.source !== 'camera') return false;
    if (filter === 'no_camera' && s.state !== 'no_camera') return false;
    if ((filter === 'normal' || filter === 'busy' || filter === 'alert') && s.status !== filter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (![s.name, s.id, ...s.features, ...s.cameras.map(c => c.id)].some(t => t.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const activeZone = zones.find(z => z.z === selectedZone);
  // people seen now on all sector cameras, each camera once (a camera shared by two sectors is not counted twice)
  // the total crowd in the sectors: every sector's People now added up (16 Oct 2026, operators)
  const known = sectors.map(s => s.counts?.inside).filter((v): v is number => v != null);
  const totalNow = known.length ? known.reduce((a, b) => a + b, 0) : null;

  const FilterBtn: React.FC<{ id: Filter; label: string; dot?: string; active: string; idle: string }> = ({ id, label, dot, active, idle }) => (
    <button
      onClick={() => { setFilter(filter === id && id !== 'all' ? 'all' : id); if (id === 'all') setSelectedZone('all'); }}
      className={`px-2.5 py-1 rounded-md text-xs font-semibold transition cursor-pointer flex items-center gap-1.5 ${filter === id ? active : idle}`}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
      <span>{label} ({counts[id]})</span>
    </button>
  );

  return (
    <div className={`w-full text-neutral-900 dark:text-neutral-100 font-sans space-y-2.5 transition-colors duration-200 ${embedded ? '' : 'min-h-screen bg-[#f4f5f8] dark:bg-[#111215] p-2.5 sm:p-3'}`}>
      {embedded && <h2 className="px-1 text-xl font-black tracking-tight text-[#84052c] dark:text-rose-200">Sector Flow & Live Occupancy</h2>}
      {/* Header & KPI ribbon */}
      <div className="bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl p-2.5 sm:px-3.5 sm:py-3 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5 pb-2.5 border-b border-neutral-100 dark:border-[#222733]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-blue-50 border border-blue-200/80 dark:bg-blue-950/40 dark:border-blue-500/20"
                  title="Total people now in all sectors (the People now of every sector added up)">
                  <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-xs sm:text-[13px] font-semibold text-blue-700 dark:text-blue-300">Total in sectors</span>
                  <strong className="text-lg sm:text-xl font-black font-mono text-neutral-900 dark:text-neutral-50 leading-none">{totalNow != null ? totalNow.toLocaleString('en-IN') : '—'}</strong>
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setWallOpen(true)} disabled={!sectors.some(s => s.cameras.length)}
              title="Play every sector's cameras one after another"
              className="px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-xs sm:text-[13px] font-semibold flex items-center gap-1.5 cursor-pointer shadow-2xs">
              <MonitorPlay className="w-4 h-4" />Streaming
            </button>
            <div className="relative">
              <Search className="w-4 h-4 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Search sector / landmark / camera..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="bg-neutral-50 dark:bg-[#14171f] border border-neutral-200/80 dark:border-[#262b37] rounded-lg pl-8.5 pr-2.5 py-1.5 text-xs sm:text-[13px] text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-hidden focus:border-blue-500 w-[190px] sm:w-[240px] shadow-2xs"
              />
            </div>
            <div className="bg-neutral-100 dark:bg-[#14171f] p-1 rounded-lg inline-flex flex-wrap items-center gap-1 border border-neutral-200/60 dark:border-[#222733]">
              <FilterBtn id="all" label="All" active="bg-blue-500 text-white shadow-xs" idle="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100" />
              <FilterBtn id="live" label="Live" dot="bg-blue-500" active="bg-blue-500 text-white shadow-xs" idle="text-blue-600 dark:text-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-950/30" />
              <FilterBtn id="no_camera" label="No camera" dot="bg-neutral-400" active="bg-neutral-500 text-white shadow-xs" idle="text-neutral-500 dark:text-neutral-400 hover:bg-neutral-200/50 dark:hover:bg-[#202532]" />
              <FilterBtn id="normal" label="Normal" dot="bg-emerald-500" active="bg-emerald-500 text-white shadow-xs" idle="text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30" />
              <FilterBtn id="busy" label="Busy" dot="bg-amber-500" active="bg-amber-500 text-white shadow-xs" idle="text-amber-600 dark:text-amber-400 hover:bg-amber-50/50 dark:hover:bg-amber-950/30" />
              <FilterBtn id="alert" label="Alert" dot="bg-rose-500" active="bg-rose-500 text-white shadow-xs" idle="text-rose-600 dark:text-rose-400 hover:bg-rose-50/50 dark:hover:bg-rose-950/30" />
            </div>
          </div>
        </div>
      </div>

      {!embedded && <DarshanJourneySummary />}

      {error && outage && (
        <div className="px-3 py-2 rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-500 text-xs font-semibold">{error}</div>
      )}

      {selectedZone !== 'all' && activeZone && (
        <div className="bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl px-3.5 py-2 flex items-center justify-between shadow-xs">
          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              onClick={() => setSelectedZone('all')}
              className="px-2.5 py-1 rounded-md bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/40 border border-blue-200/80 dark:border-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-semibold cursor-pointer flex items-center gap-1.5"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>All Zones</span>
            </button>
            <span className="text-neutral-300 dark:text-neutral-600">•</span>
            <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-blue-500 text-white shadow-2xs">ZONE {activeZone.z}</span>
            <span className="font-bold text-neutral-900 dark:text-neutral-100 text-xs sm:text-sm">{activeZone.name}</span>
            <span className="text-xs text-neutral-400">({activeZone.name_te})</span>
          </div>
          <div className="text-xs font-mono text-neutral-500 dark:text-neutral-400">{filtered.length} sectors</div>
        </div>
      )}

      {/* Sector cards */}
      <div className="w-full">
        {data && filtered.length === 0 ? (
          <div className="h-56 flex flex-col items-center justify-center text-center bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl p-4 shadow-xs">
            <CheckCircle2 className="w-8 h-8 text-neutral-400 mb-2" />
            <div className="text-sm text-neutral-700 dark:text-neutral-300 font-semibold">No sectors match your search or filter</div>
            <p className="text-xs text-neutral-400 mt-0.5">Try "All" or clear the search box</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5">
            {filtered.map(s => {
              const { badge, tone } = look(s);
              const t = TONE[tone];
              const hasCams = s.source === 'camera';
              return (
                <div key={s.id} className={`p-3 rounded-xl border flex flex-col justify-between transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xs ${t.card}`}>
                  <div>
                    <div className="flex items-center justify-between gap-1 mb-1.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-mono text-xs sm:text-[13px] font-bold text-neutral-900 dark:text-neutral-100 px-2 py-0.5 rounded bg-neutral-100 dark:bg-[#202532] border border-neutral-200/60 dark:border-[#2a3040] shrink-0">{s.id}</span>
                      </div>
                      <span className={`font-mono text-[10px] px-2 py-0.5 rounded font-bold tracking-wider uppercase border flex items-center gap-1 shrink-0 ${t.badge}`}>
                        {tone === 'neutral' ? <CameraOff className="w-3 h-3" /> : <span className={`w-1.5 h-1.5 rounded-full ${t.dot} ${tone === 'rose' || tone === 'blue' ? 'animate-pulse' : ''}`} />}
                        <span>{badge}</span>
                      </span>
                    </div>
                    <div className="font-bold text-xs sm:text-[13px] text-neutral-900 dark:text-neutral-100 leading-snug my-1 truncate" title={s.name}>{s.name}</div>
                    <div className="flex flex-wrap gap-1 my-1">
                      {s.features.map((f, i) => (
                        <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-50 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733] text-neutral-500 dark:text-neutral-400 truncate max-w-full">{f}</span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-2 pt-2 border-t border-neutral-100 dark:border-[#222733] flex flex-col gap-1.5">
                    {hasCams ? (
                      <>
                        {/* 16 Oct 2026, operators: inside now, entering and leaving - the figures the ground team acts on */}
                        <div className="rounded-lg bg-blue-50/70 dark:bg-blue-950/25 border border-blue-200/70 dark:border-blue-500/20 px-2.5 py-1.5"
                          title="People now = the crowd inside the sector, between its start and end camera">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-blue-700 dark:text-blue-300"><Users className="w-4 h-4" />People now</div>
                            <div className="font-black font-mono text-2xl leading-none text-neutral-900 dark:text-neutral-50">{fmt(s.counts?.inside)}</div>
                          </div>
                          {/* 16 Oct 2026, operators: how long it takes to move through the sector = people now / leaving a minute */}
                          {s.counts?.inside != null && (s.counts?.leaving_per_min ?? 0) >= 1 && (
                            <div className="flex items-center justify-between mt-0.5 text-[11px] text-blue-700/80 dark:text-blue-300/80"
                              title="Time to move through the sector = people now ÷ people leaving a minute (last 15 min, with the sector\'s correction factor, as in its count)">
                              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Time here</span>
                              <b className="font-mono text-sm text-neutral-900 dark:text-neutral-50">≈ {(() => { const m = s.counts!.inside! / (s.counts!.leaving_per_min! * ((s.counts as any).calibration ?? 1)); return m >= 60 ? `${Math.floor(m / 60)} h ${String(Math.round(m % 60)).padStart(2, '0')} m` : `${Math.round(m)} min`; })()}</b>
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-1.5 text-[11px] font-mono">
                          <div className="rounded bg-neutral-50 dark:bg-[#14171f] px-2 py-1" title="People crossing the sector's start line per minute (last 15 min)">
                            <div className="text-neutral-400 flex items-center gap-0.5 text-[10px]"><ArrowUpRight className="w-3 h-3 text-emerald-500" />Entering /min</div>
                            <div className="font-bold text-neutral-900 dark:text-neutral-100 text-sm sm:text-base leading-tight">{fmt(s.counts?.entering_per_min)}</div>
                          </div>
                          <div className="rounded bg-neutral-50 dark:bg-[#14171f] px-2 py-1" title="People crossing the sector's end line per minute (last 15 min)">
                            <div className="text-neutral-400 flex items-center gap-0.5 text-[10px]"><ArrowDownRight className="w-3 h-3 text-sky-500" />Leaving /min</div>
                            <div className="font-bold text-neutral-900 dark:text-neutral-100 text-sm sm:text-base leading-tight">{fmt(s.counts?.leaving_per_min)}</div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-[11px] font-mono text-neutral-500 dark:text-neutral-400">
                          <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />Today</span>
                          <span><b className="text-emerald-600 dark:text-emerald-400 text-xs sm:text-[13px] font-bold">{fmt(s.counts?.entered_today)}</b> entered · <b className="text-sky-600 dark:text-sky-400 text-xs sm:text-[13px] font-bold">{fmt(s.counts?.left_today)}</b> left</span>
                        </div>
                        {(s.counts?.cameras_down?.length ?? 0) > 0 && (
                          <div className="text-[10.5px] font-mono text-rose-500">Camera down: {s.counts!.cameras_down.join(', ')} - figures paused</div>
                        )}
                      </>
                    ) : (
                      <div className="text-[11px] font-mono text-neutral-400 py-1">No camera mapped to this sector yet</div>
                    )}

                    <div className="flex items-center justify-between text-[11px] font-mono text-neutral-500 dark:text-neutral-400 pt-0.5 gap-1">
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => hasCams && setOpenSector(s)}
                          disabled={!hasCams}
                          className="flex items-center gap-1.5 text-neutral-600 dark:text-neutral-300 hover:text-blue-600 dark:hover:text-blue-400 transition cursor-pointer disabled:cursor-default disabled:opacity-50 p-1 px-1.5 rounded bg-neutral-50 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733]"
                          title={hasCams ? 'View live camera streams' : 'No camera in this sector'}
                        >
                          <Video className="w-3.5 h-3.5 text-blue-500" />
                          <span>{hasCams ? `${s.cameras_online}/${s.cameras_total} cams` : '0 cams'}</span>
                        </button>
                      </div>
                      {hasCams && s.counts && (
                        <span className="text-right truncate text-neutral-400" title="People who entered / left the sector in the last 15 minutes">
                          15 min: {fmt(s.counts.entered_15m)} in / {fmt(s.counts.left_15m)} out
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {wallOpen && <SectorVideoWall sectors={sectors} onClose={() => setWallOpen(false)} />}

      {openSector && (
        <SectorCameraModal
          sector={sectors.find(s => s.id === openSector.id) ?? openSector}
          onClose={() => setOpenSector(null)}
        />
      )}
    </div>
  );
};
